/* ============================================================
   IntakeCard — the unified "We scanned your site" review card
   that drops inline in the onboarding chat. Confirm-first: it
   merges brand identity (logo + colors + style) AND the business
   profile (what you do / who / how / where) from two scans into
   one card. Nothing persists until the primary CTA.

   Ported from onbintake-pack (onbintake-0X-*.html). Pure-ish
   presentational: all detected data + the persist/skip callbacks
   come in as props; the card owns only local keep/drop/edit state
   and emits the confirmed { logo, colors, fields } to onConfirm.

   States: 'detecting' | 'found' | 'not_found' (partial = found
   with empty fields → dashed "add" affordances + amber pill).
   ============================================================ */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isSvgFile, rasterizeSvgToPng } from '../../lib/rasterize.js';
import { platformDisplayName } from '../../lib/platforms.js';

const HEX_RE = /^#?[0-9a-fA-F]{3,8}$/;
const normHex = (v) => {
  const s = String(v || '').trim();
  if (!HEX_RE.test(s)) return null;
  return (s.startsWith('#') ? s : `#${s}`).toUpperCase();
};

/* ---- inline stroke icons (1:1 with the pack) ---- */
const S = ({ d, fill = false, w = 2, size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill ? 'currentColor' : 'none'}
       stroke={fill ? 'none' : 'currentColor'} strokeWidth={w} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);
const IcGlobe = (p) => <S {...p} d={['M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z', 'M3 12h18', 'M12 3a15 15 0 0 1 0 18', 'M12 3a15 15 0 0 0 0 18']} />;
const IcUpload = (p) => <S {...p} d={['M4 17v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3', 'M12 16V4', 'M7 9l5-5 5 5']} />;
const IcCheck = (p) => <S {...p} d="m5 12 5 5 9-11" />;
const IcPlus = (p) => <S {...p} d={['M12 5v14', 'M5 12h14']} />;
const IcX = (p) => <S {...p} d={['M6 6 18 18', 'M18 6 6 18']} />;
const IcHouse = (p) => <S {...p} d="M3 9 12 3l9 6v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" />;
const IcPeople = (p) => <S {...p} d={['M9 11.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z', 'M2 21a7 7 0 0 1 14 0', 'M17 4a3.5 3.5 0 0 1 0 7']} />;
const IcChat = (p) => <S {...p} d="M21 11.5a8.4 8.4 0 0 1-12.8 7.5L3 21l1.9-5.2A8.4 8.4 0 1 1 21 11.5z" />;
const IcTrend = (p) => <S {...p} d={['m3 17 6-6 4 4 8-8', 'M14 7h7v7']} />;
const IcTarget = (p) => <S {...p} d={['M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z', 'M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z']} />;
const IcArrow = (p) => <S {...p} d={['M5 12h14', 'M13 5l7 7-7 7']} />;
const IcSparkle = (p) => <S {...p} fill d="M12 3 13.5 8 18 9.5 13.5 11 12 16 10.5 11 6 9.5 10.5 8z" />;
const IcFace = (p) => <S {...p} d={['M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z', 'M9 9h.01', 'M15 9h.01', 'M8.5 15a4 4 0 0 1 7 0']} />;
const IcExpand = (p) => <S {...p} d={['M8 3H5a2 2 0 0 0-2 2v3', 'M16 3h3a2 2 0 0 1 2 2v3', 'M21 16v3a2 2 0 0 1-2 2h-3', 'M3 16v3a2 2 0 0 0 2 2h3']} />;
const IcPencil = (p) => <S {...p} d={['M12 20h9', 'M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z']} />;

// Minimal platform glyphs (display tiles). Fall back to a 2-letter chip.
const PLAT = {
  instagram: <S d={['M7 3h10a4 4 0 0 1 4 4v10a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V7a4 4 0 0 1 4-4z', 'M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z', 'M17 6.5h.01']} size={14} />,
  facebook: <S fill d="M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.4v7A10 10 0 0 0 22 12z" size={14} />,
  tiktok: <S fill d="M19.6 6.7a4.8 4.8 0 0 1-3.8-4.2V2h-3.4v13.7a2.9 2.9 0 1 1-2.9-2.9v-3.5a6.3 6.3 0 1 0 6.3 6.3V8.9a8.2 8.2 0 0 0 4.8 1.5V7a4.8 4.8 0 0 1-1-.3z" size={14} />,
  linkedin: <S fill d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14zM8.3 18V10.2H5.8V18h2.5zM7 9.1a1.45 1.45 0 1 0 0-2.9 1.45 1.45 0 0 0 0 2.9zM18.2 18v-4.3c0-2.3-1.2-3.4-2.9-3.4-1.3 0-1.9.7-2.3 1.3v-1.1h-2.5V18h2.5v-4.2c0-.2 0-.5.1-.6.2-.5.6-1 1.3-1 .9 0 1.3.7 1.3 1.7V18h2.5z" size={14} />,
  x: <S fill d="M17.5 3h3l-6.6 7.5L21.8 21h-5.9l-4.3-5.6L6.5 21H3.4l7-8L2.6 3h6l3.9 5.1L17.5 3z" size={13} />,
  telegram: <S fill d="M21.9 4.3 18.7 19.5c-.2 1-.9 1.3-1.7.8l-4.7-3.5-2.3 2.2c-.3.3-.5.5-1 .5l.3-4.8 8.7-7.9c.4-.3-.1-.5-.6-.2L6.6 13 2 11.6c-1-.3-1-1 .2-1.5l18.2-7c.8-.3 1.6.2 1.5 1.2z" size={14} />,
  youtube: <S fill d="M23 12s0-3.2-.4-4.7a2.5 2.5 0 0 0-1.7-1.7C19.4 5.2 12 5.2 12 5.2s-7.4 0-8.9.4A2.5 2.5 0 0 0 1.4 7.3C1 8.8 1 12 1 12s0 3.2.4 4.7a2.5 2.5 0 0 0 1.7 1.7c1.5.4 8.9.4 8.9.4s7.4 0 8.9-.4a2.5 2.5 0 0 0 1.7-1.7C23 15.2 23 12 23 12zM10 15.5v-7l6 3.5-6 3.5z" size={14} />,
};
const platLabel = (k) => platformDisplayName(k);

export default function IntakeCard({
  status = 'detecting',
  variant = 'onboarding',
  data = {},
  busy = false,
  onConfirm,
  onSkip,
  onTellAbout,
}) {
  const { t } = useTranslation();
  const quickBrand = variant === 'quick-brand';
  const domain = data.domain || '';

  const detectedColors = useMemo(
    () => (Array.isArray(data.colors) ? data.colors.map(normHex).filter(Boolean) : []),
    [data.colors],
  );
  const styleTags = Array.isArray(data.styleTags) ? data.styleTags.filter(Boolean) : [];
  const biz = data.business || {};
  const platforms = Array.isArray(biz.platforms) ? biz.platforms.filter(Boolean) : [];

  // ---- local confirm-first state (seeded from props, never mutates them) ----
  const [logoState, setLogoState] = useState('keep'); // 'keep' | 'skip'
  const [logoOverride, setLogoOverride] = useState(null); // {dataUrl} from replace
  const [dropped, setDropped] = useState(() => new Set()); // dropped hex
  const [extraColors, setExtraColors] = useState([]);
  const [hexOpen, setHexOpen] = useState(false);
  const [hexDraft, setHexDraft] = useState('');
  const [overrides, setOverrides] = useState({}); // fieldKey -> string
  const [editKey, setEditKey] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [logoErr, setLogoErr] = useState(false);
  const [zoom, setZoom] = useState(false); // logo full-preview lightbox
  const [savedKey, setSavedKey] = useState(null); // field briefly flashing "saved"
  const fileRef = useRef(null);
  const editRef = useRef(null); // inline-edit textarea (auto-grown)
  const editDraftRef = useRef(''); // latest draft, read by the blur-commit handler
  const cancellingRef = useRef(false); // suppress blur-commit while cancelling
  const justOpenedRef = useRef(false); // place caret at end on first open only
  const zoomCloseRef = useRef(null);
  const logoBtnRef = useRef(null); // restore focus here when the zoom closes

  const logoSrc = logoOverride?.dataUrl || data.logo?.dataUrl || null;

  const allColors = useMemo(() => {
    const seen = new Set(); const out = [];
    for (const c of [...detectedColors, ...extraColors]) if (c && !seen.has(c)) { seen.add(c); out.push(c); }
    return out;
  }, [detectedColors, extraColors]);
  const keptColors = allColors.filter((c) => !dropped.has(c));

  const fieldVal = (key) => (key in overrides ? overrides[key] : (biz[key] || ''));

  // 4 business blocks → their text fields (array fields joined for display/edit).
  const BLOCKS = [
    { ic: <IcHouse size={12} />, titleKey: 'intake.blkDo', fields: [
      { key: 'businessDescription', labelKey: 'intake.fDescription', trunc: true },
      { key: 'offer', labelKey: 'intake.fOffer' },
    ] },
    { ic: <IcPeople size={12} />, titleKey: 'intake.blkServe', fields: [
      { key: 'targetAudience', labelKey: 'intake.fAudience', trunc: true },
      { key: 'painPoints', labelKey: 'intake.fPain' },
    ] },
    { ic: <IcChat size={12} />, titleKey: 'intake.blkSound', fields: [
      { key: 'toneOfVoice', labelKey: 'intake.fTone' },
      { key: 'contentPillars', labelKey: 'intake.fPillars' },
    ] },
    { ic: <IcTrend size={12} />, titleKey: 'intake.blkWhere', platforms: true, fields: [
      { key: 'goals', labelKey: 'intake.fGoal', goal: true },
    ] },
  ];

  const startEdit = (key) => { cancellingRef.current = false; justOpenedRef.current = true; setEditKey(key); setEditDraft(fieldVal(key)); };
  // Commit the current edit. Save-on-blur calls this too, so it must no-op when
  // nothing is open or a cancel (Esc / ✕) is in flight, and it flashes the field.
  const saveEdit = () => {
    const key = editKey;
    if (key == null || cancellingRef.current) { cancellingRef.current = false; return; }
    // Read the textarea's live DOM value (the source of truth) so the commit is
    // correct regardless of React state-sync timing on a fast type→blur.
    const val = (editRef.current?.value ?? editDraftRef.current ?? '').trim();
    setOverrides((o) => ({ ...o, [key]: val }));
    setEditKey(null); setEditDraft('');
    setSavedKey(key);
    window.setTimeout(() => setSavedKey((k) => (k === key ? null : k)), 900);
  };
  const cancelEdit = () => { cancellingRef.current = true; setEditKey(null); setEditDraft(''); };

  useEffect(() => { editDraftRef.current = editDraft; }, [editDraft]);

  // Grow the inline-edit textarea to fit its content (full value visible) and,
  // on first open, drop the caret at the end so the user continues their text.
  useEffect(() => {
    const el = editRef.current;
    if (!editKey || !el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 260)}px`;
    if (justOpenedRef.current) {
      justOpenedRef.current = false;
      const len = el.value.length;
      el.focus();
      el.setSelectionRange(len, len);
    }
  }, [editKey, editDraft]);

  // Zoom lightbox: Esc closes; focus moves to the close button and returns to
  // the logo when it closes, so keyboard focus never gets lost behind it.
  useEffect(() => {
    if (!zoom) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setZoom(false); };
    document.addEventListener('keydown', onKey);
    zoomCloseRef.current?.focus();
    return () => { document.removeEventListener('keydown', onKey); logoBtnRef.current?.focus(); };
  }, [zoom]);

  const toggleColor = (c) => setDropped((prev) => { const n = new Set(prev); n.has(c) ? n.delete(c) : n.add(c); return n; });
  const addHex = () => { const h = normHex(hexDraft); if (!h) return; if (!allColors.includes(h)) setExtraColors((p) => [...p, h]); setHexOpen(false); setHexDraft(''); };

  const onPickLogo = async (e) => {
    const file = e.target.files?.[0]; e.target.value = ''; if (!file) return;
    setLogoErr(false);
    try {
      let f = file;
      if (isSvgFile(file)) f = await rasterizeSvgToPng(file);
      const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = () => rej(r.error); r.readAsDataURL(f); });
      setLogoOverride({ dataUrl }); setLogoState('keep');
    } catch { setLogoErr(true); }
  };

  // coverage: kept primary slots / total defined slots
  const slots = [
    Boolean(logoSrc && logoState === 'keep'),
    keptColors.length > 0,
    ...['businessDescription', 'offer', 'targetAudience', 'painPoints', 'toneOfVoice', 'contentPillars', 'goals'].map((k) => Boolean(fieldVal(k))),
    platforms.length > 0,
  ];
  const have = slots.filter(Boolean).length;
  const total = slots.length;
  const pillVariant = have >= total ? 'found' : 'partial';

  const commit = () => {
    const fields = {};
    ['businessDescription', 'offer', 'targetAudience', 'painPoints', 'toneOfVoice', 'contentPillars', 'goals', 'productOrService', 'visualStyle'].forEach((k) => {
      const v = fieldVal(k); if (typeof v === 'string' && v.trim()) fields[k] = v.trim();
    });
    onConfirm?.({
      logo: logoState === 'keep' && logoSrc ? { dataUrl: logoOverride?.dataUrl || data.logo?.dataUrl || null } : null,
      colors: keptColors,
      fields,
    });
  };

  /* ---------- DETECTING (shimmer skeleton) ---------- */
  if (status === 'detecting') {
    return (
      <div className="ob-ic" data-state="detecting" role="status" aria-live="polite">
        <div className="ob-ic-head">
          <div>
            <span className="ob-ic-eyebrow">{t('onboardingChat.intake.eyebrow')}</span>
            <h3 className="ob-ic-h">{t('onboardingChat.intake.detectingTitle', { domain })}</h3>
            <span className="ob-ic-src"><span className="ob-ic-spin" />{t('onboardingChat.intake.detectingNote')}</span>
          </div>
          <span className="ob-ic-pill ob-ic-pill-scan"><span className="ob-ic-spin" />{t('onboardingChat.intake.pillScanning')}</span>
        </div>
        <div className="ob-ic-zone">
          <div className="ob-ic-zl"><span>{t('onboardingChat.intake.zoneBrand')}</span><span className="ob-ic-ln" /></div>
          <div className="ob-ic-logo-row">
            <div className="ob-ic-sk ob-ic-sk-logo" />
            <div style={{ flex: 1 }}><div className="ob-ic-sk ob-ic-sk-line" style={{ width: '40%' }} /><div className="ob-ic-sk ob-ic-sk-line" style={{ width: '65%', height: 8 }} /></div>
          </div>
          <div className="ob-ic-swatches">{[0, 1, 2].map((i) => <span key={i} className="ob-ic-sk" style={{ width: 84, height: 28, borderRadius: 999 }} />)}</div>
        </div>
        <div className="ob-ic-zone">
          <div className="ob-ic-zl"><span>{t('onboardingChat.intake.zoneBiz')}</span><span className="ob-ic-ln" /></div>
          <div className="ob-ic-blocks">{[0, 1, 2, 3].map((i) => (
            <div key={i} className="ob-ic-blk">
              <div className="ob-ic-sk ob-ic-sk-line" style={{ width: '38%', height: 8, marginBottom: 11 }} />
              <div className="ob-ic-sk ob-ic-sk-line" style={{ width: '90%' }} />
              <div className="ob-ic-sk ob-ic-sk-line" style={{ width: '70%' }} />
            </div>
          ))}</div>
        </div>
        <div className="ob-ic-foot"><div className="ob-ic-fallback">{t('onboardingChat.intake.detectingFoot')}</div></div>
      </div>
    );
  }

  /* ---------- NOT FOUND (collapsed manual fallback) ---------- */
  if (status === 'not_found') {
    return (
      <div className="ob-ic" data-state="not_found">
        <div className="ob-ic-head">
          <div>
            <span className="ob-ic-eyebrow">{t('onboardingChat.intake.eyebrow')}</span>
            <h3 className="ob-ic-h">{t('onboardingChat.intake.nfTitle')}</h3>
          </div>
          <span className="ob-ic-pill ob-ic-pill-empty"><span className="ob-ic-dot" />{t('onboardingChat.intake.pillEmpty')}</span>
        </div>
        <div className="ob-ic-nf">
          <div className="ob-ic-nf-ic"><IcFace size={24} /></div>
          <h4>{t('onboardingChat.intake.nfHeading')}</h4>
          <p>{t('onboardingChat.intake.nfBody')}</p>
          <div className="ob-ic-nf-acts">
            <button type="button" className="ob-ic-nf-tell" onClick={() => onTellAbout?.()}><IcSparkle size={14} />{t('onboardingChat.intake.nfTell')}</button>
            <button type="button" className="ob-ic-nf-up" onClick={() => fileRef.current?.click()}><IcUpload size={14} />{t('onboardingChat.intake.nfUpload')}</button>
          </div>
        </div>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml" hidden onChange={(e) => { onPickLogo(e); }} />
      </div>
    );
  }

  /* ---------- FOUND / PARTIAL (the centerpiece) ---------- */
  return (
    <div className="ob-ic" data-state="found">
      <div className="ob-ic-head">
        <div>
          <span className="ob-ic-eyebrow">{t('onboardingChat.intake.eyebrow')}</span>
          <h3 className="ob-ic-h">{t('onboardingChat.intake.foundPre')} <span className="ob-ic-ac">{t('onboardingChat.intake.foundAccent')}</span>{t('onboardingChat.intake.foundPost')}</h3>
          {domain && <span className="ob-ic-src"><IcGlobe size={12} />{t('onboardingChat.intake.pulledFrom')} <span className="ob-ic-u">{domain}</span></span>}
        </div>
        <span className={`ob-ic-pill ob-ic-pill-${pillVariant}`}><span className="ob-ic-dot" />{pillVariant === 'found' ? t('onboardingChat.intake.pillFound') : t('onboardingChat.intake.pillPartial')}</span>
      </div>

      {/* ZONE A — brand identity */}
      <div className="ob-ic-zone">
        <div className="ob-ic-zl"><span>{t('onboardingChat.intake.zoneBrand')}</span><span className="ob-ic-ln" /></div>
        <div className={`ob-ic-logo-wrap ${logoState === 'skip' ? 'is-skip' : ''}`}>
          <button ref={logoBtnRef} type="button" className="ob-ic-logo-preview" onClick={() => { if (logoSrc) setZoom(true); }} aria-label={logoSrc ? t('onboardingChat.intake.viewFull') : undefined} title={logoSrc ? t('onboardingChat.intake.viewFull') : undefined}>
            {logoSrc
              ? <><img src={logoSrc} alt={t('onboardingChat.intake.logoAlt')} /><span className="ob-ic-logo-expand"><IcExpand size={13} /></span></>
              : <div className="ob-ic-logo-ph">{(domain[0] || 'B').toUpperCase()}</div>}
          </button>
          <div className="ob-ic-logo-bar">
            <div className="ob-ic-logo-info">
              <span className="ob-ic-nm">{t('onboardingChat.intake.logo')}</span>
              <span className="ob-ic-pulled"><IcUpload size={10} />{domain ? t('onboardingChat.intake.pulledFromDomain', { domain }) : t('onboardingChat.intake.pulled')}</span>
            </div>
            <div className="ob-ic-mini">
              <button type="button" className={`ob-ic-mb${logoState === 'keep' ? ' is-keep' : ''}`} onClick={() => setLogoState('keep')} aria-pressed={logoState === 'keep'} disabled={!logoSrc}><IcCheck size={11} />{t('onboardingChat.intake.keep')}</button>
              <button type="button" className="ob-ic-mb" onClick={() => fileRef.current?.click()}>{t('onboardingChat.intake.replace')}</button>
              <button type="button" className="ob-ic-mb ob-ic-skip" onClick={() => setLogoState('skip')}>{t('onboardingChat.intake.skip')}</button>
            </div>
          </div>
          {logoErr && <div className="ob-ic-logo-err">{t('onboardingChat.intake.logoErr')}</div>}
        </div>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml" hidden onChange={onPickLogo} />

        <div className="ob-ic-subl">{allColors.length ? t('onboardingChat.intake.colorsTap') : t('onboardingChat.intake.colors')}</div>
        {allColors.length > 0 ? (
          <div className="ob-ic-swatches">
            {allColors.map((c) => (
              <button type="button" key={c} className={`ob-ic-sw ${dropped.has(c) ? 'is-drop' : ''}`} onClick={() => toggleColor(c)} aria-pressed={!dropped.has(c)} aria-label={`${c} — ${dropped.has(c) ? t('onboardingChat.intake.colorOff') : t('onboardingChat.intake.colorOn')}`} title={dropped.has(c) ? t('onboardingChat.intake.colorOff') : t('onboardingChat.intake.colorOn')}>
                <i style={{ background: c }} /><span className="ob-ic-hex">{c}</span><span className="ob-ic-sw-ck">{dropped.has(c) ? <IcPlus size={11} /> : <IcCheck size={12} />}</span>
              </button>
            ))}
            {hexOpen ? (
              <span className="ob-ic-hexrow">
                <span className="ob-ic-pv" style={{ background: normHex(hexDraft) || 'transparent' }} />
                <input value={hexDraft} onChange={(e) => setHexDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addHex(); if (e.key === 'Escape') setHexOpen(false); }} placeholder="#1A1A1A" maxLength={9} autoFocus />
                <button type="button" className="ob-ic-edsave" onClick={addHex} disabled={!normHex(hexDraft)}><IcCheck size={12} /></button>
                <button type="button" className="ob-ic-edcancel" onClick={() => { setHexOpen(false); setHexDraft(''); }}><IcX size={11} /></button>
              </span>
            ) : (
              <button type="button" className="ob-ic-swadd" onClick={() => setHexOpen(true)}><IcPlus size={12} />{t('onboardingChat.intake.hex')}</button>
            )}
          </div>
        ) : (
          <div className="ob-ic-empty"><span className="ob-ic-empty-t">{t('onboardingChat.intake.colorsNone')}</span>{hexOpen ? (
            <span className="ob-ic-hexrow">
              <span className="ob-ic-pv" style={{ background: normHex(hexDraft) || 'transparent' }} />
              <input value={hexDraft} onChange={(e) => setHexDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addHex(); }} placeholder="#1A1A1A" maxLength={9} autoFocus />
              <button type="button" className="ob-ic-edsave" onClick={addHex} disabled={!normHex(hexDraft)}><IcCheck size={12} /></button>
            </span>
          ) : (<button type="button" className="ob-ic-swadd" onClick={() => setHexOpen(true)}><IcPlus size={12} />{t('onboardingChat.intake.addPalette')}</button>)}</div>
        )}

        {styleTags.length > 0 && (
          <span className="ob-ic-style"><span className="ob-ic-style-lab">{t('onboardingChat.intake.looksLike')}</span><span className="ob-ic-style-val">{styleTags.join(' · ')}</span></span>
        )}
      </div>

      {/* ZONE B — business profile */}
      <div className="ob-ic-zone">
        <div className="ob-ic-zl"><span>{t('onboardingChat.intake.zoneBiz')}</span><span className="ob-ic-ln" /></div>
        <div className="ob-ic-blocks">
          {BLOCKS.map((blk, bi) => (
            <div key={bi} className="ob-ic-blk">
              <div className="ob-ic-bl"><span className="ob-ic-blt">{blk.ic}{t(`onboardingChat.${blk.titleKey}`)}</span><span className="ob-ic-srctag">{t('onboardingChat.intake.fromSite')}</span></div>
              {blk.fields.map((f) => {
                const val = fieldVal(f.key);
                const editing = editKey === f.key;
                if (editing) {
                  return (
                    <div key={f.key} className="ob-ic-fld">
                      <div className="ob-ic-fl">{t(`onboardingChat.${f.labelKey}`)}</div>
                      <div className="ob-ic-editing">
                        <textarea ref={editRef} value={editDraft} aria-label={t(`onboardingChat.${f.labelKey}`)} onChange={(e) => setEditDraft(e.target.value)} onBlur={saveEdit} onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); saveEdit(); } if (e.key === 'Escape') cancelEdit(); }} rows={2} />
                      </div>
                      <div className="ob-ic-edrow">
                        <span className="ob-ic-edhint"><kbd>⌘↵</kbd> {t('onboardingChat.intake.saveKb')} · <kbd>esc</kbd> {t('onboardingChat.intake.cancelKb')}</span>
                        <span className="ob-ic-edrow-sp" />
                        <button type="button" className="ob-ic-edcancel" onMouseDown={(e) => { e.preventDefault(); cancelEdit(); }} aria-label={t('onboardingChat.intake.close')}><IcX size={13} /></button>
                        <button type="button" className="ob-ic-edsave" onMouseDown={(e) => { e.preventDefault(); saveEdit(); }} aria-label={t('onboardingChat.intake.saveHint')} title={t('onboardingChat.intake.saveHint')}><IcCheck size={14} /></button>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={f.key} className="ob-ic-fld">
                    <div className="ob-ic-fl">{t(`onboardingChat.${f.labelKey}`)}</div>
                    {val
                      ? <div className={`ob-ic-fv ${savedKey === f.key ? 'is-saved' : ''}`} role="button" tabIndex={0} aria-label={`${t(`onboardingChat.${f.labelKey}`)} — ${t('onboardingChat.intake.editHint')}`} onClick={() => startEdit(f.key)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); startEdit(f.key); } }}>{val}<span className="ob-ic-fv-pen" aria-hidden="true"><IcPencil size={12} /></span><span className="ob-ic-fv-edit">{t('onboardingChat.intake.editHint')}</span></div>
                      : <button type="button" className="ob-ic-swadd ob-ic-fldadd" onClick={() => startEdit(f.key)}><IcPlus size={12} />{t('onboardingChat.intake.addField', { field: t(`onboardingChat.${f.labelKey}`) })}</button>}
                  </div>
                );
              })}
              {blk.platforms && (
                <div className="ob-ic-fld">
                  <div className="ob-ic-fl">{t('onboardingChat.intake.fPlatforms')}</div>
                  {platforms.length
                    ? <div className="ob-ic-platrow">{platforms.slice(0, 6).map((p) => <span key={p} className="ob-ic-pg" title={platLabel(p)}>{PLAT[p] || <span className="ob-ic-pg-txt">{platLabel(p).slice(0, 2)}</span>}</span>)}</div>
                    : <span className="ob-ic-muted">{t(quickBrand ? 'quickBrand.platformsLater' : 'onboardingChat.intake.platformsLater')}</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* footer */}
      <div className="ob-ic-foot">
        <div className="ob-ic-fallback">{t(quickBrand ? 'quickBrand.reviewHint' : 'onboardingChat.intake.fallback')}</div>
        <div className="ob-ic-footrow">
          <span className="ob-ic-cov"><span className="ob-ic-bar"><i style={{ width: `${Math.round((have / total) * 100)}%` }} /></span>{t('onboardingChat.intake.coverage', { have, total })}</span>
          <span className="ob-ic-sp" />
          <button type="button" className="ob-ic-b2" disabled={busy} onClick={() => onSkip?.()}>{t(quickBrand ? 'quickBrand.reviewBack' : 'onboardingChat.intake.skipAll')}</button>
          <button type="button" className="ob-ic-b1" disabled={busy} onClick={commit}>{busy ? t('onboardingChat.intake.applying') : t(quickBrand ? 'quickBrand.create' : 'onboardingChat.intake.continue')} <IcArrow size={13} /></button>
        </div>
      </div>

      {zoom && logoSrc && (
        <div className="ob-ic-zoom" role="dialog" aria-modal="true" aria-label={t('onboardingChat.intake.viewFull')} onClick={() => setZoom(false)}>
          <div className="ob-ic-zoom-inner" onClick={(e) => e.stopPropagation()}>
            <img src={logoSrc} alt={t('onboardingChat.intake.logoAlt')} />
            <button ref={zoomCloseRef} type="button" className="ob-ic-zoom-x" onClick={() => setZoom(false)} aria-label={t('onboardingChat.intake.close')}><IcX size={16} /></button>
          </div>
        </div>
      )}
    </div>
  );
}
